/**
 * Normalisation d'un numéro de mobile français vers le format local (06/07…).
 *
 * Format canonique EN BASE : `0XXXXXXXXX` (celui de CGIA/review et de
 * `frPhoneNormalize` côté backend). Les widgets `react-phone-input-2` travaillent
 * en `33XXXXXXXXX` : `toPhoneInputValue` / `toFrPhoneInputValue` pour l'affichage,
 * `toStorage` à la soumission.
 *
 * Convertit les préfixes internationaux français (`0033…`, `+33…`, `033…`, `33…`)
 * en `0`, et retire les séparateurs. Répare aussi les formes « indicatif
 * redoublé » produites par une mauvaise conversion OCR / widget
 * (`0337…`, `0633…` → cœur `33[67]…` intact).
 *
 * Un numéro ÉTRANGER (autre indicatif que +33) est renvoyé tel quel — utiliser
 * `isFrMobile` pour le détecter.
 */

/** Forme internationale intacte (`33` + mobile) encadrée de parasites (cf. backend). */
const CORE_WITH_PARASITE = /^(?:0|00|0?[67])?(33[67]\d{8})0?$/;

export const normalizeFrMobile = (raw: unknown): string => {
  const source = String(raw ?? '').trim();
  if (!source) return '';

  const stripped = source.replace(/[\s.\-()]/g, '');
  const digits = stripped.replace(/\D/g, '');
  if (!digits) return '';

  // Indicatif étranger (+49, +262…) : ne pas déformer.
  if (stripped.startsWith('+') && !digits.startsWith('33')) {
    return stripped;
  }

  // Indicatif FR collé, sous ses écritures sans « + », avec exactement 9 chiffres après.
  for (const prefix of ['0033', '033', '33'] as const) {
    if (digits.startsWith(prefix) && digits.length - prefix.length === 9) {
      return `0${digits.slice(prefix.length)}`;
    }
  }

  if (/^0[1-9]\d{8}$/.test(digits)) {
    return digits;
  }

  const core = CORE_WITH_PARASITE.exec(digits);
  if (core) {
    return `0${core[1].slice(2)}`;
  }

  return stripped;
};

/**
 * Format canonique à persister en base (`CG_Entities.Phone`).
 * Idempotent : `toStorage(toStorage(x)) === toStorage(x)` pour les numéros FR.
 */
export const toStorage = (raw: unknown): string => {
  const normalized = normalizeFrMobile(raw);
  if (/^0\d{9}$/.test(normalized)) {
    return normalized;
  }
  if (!String(raw ?? '').trim()) {
    return '';
  }
  return normalized;
};

/** Vrai si la valeur, une fois normalisée, est un mobile français (06/07 + 8 chiffres). */
export const isFrMobile = (raw: unknown): boolean => /^0[67]\d{8}$/.test(normalizeFrMobile(raw));

/**
 * Convertit un numéro FR vers le format attendu par `react-phone-input-2` :
 * indicatif pays + numéro national SANS le « + » ni le 0 initial
 * (ex : "0663599456" → "33663599456"). C'est ce format qui fait afficher le
 * widget « +33 6 63 59 94 56 ». Sans conversion, la valeur nationale brute
 * "0663599456" est mal interprétée (affichage « +33 0X… » / second 33 collé —
 * retour François 2026-09 : « parfois des +3333 »).
 *
 * Un numéro déjà au format "33XXXXXXXXX" est renvoyé tel quel. Une valeur vide
 * ou non exploitable renvoie une chaîne vide.
 */
export const toFrPhoneInputValue = (raw: unknown): string => {
  const national = normalizeFrMobile(raw);
  if (/^0\d{9}$/.test(national)) {
    return `33${national.slice(1)}`;
  }
  const digits = String(raw ?? '').replace(/[\s.\-()+]/g, '');
  if (/^33\d{9}$/.test(digits)) {
    return digits;
  }
  if (/^0033\d{9}$/.test(digits)) {
    return digits.slice(2);
  }
  return '';
};

/** Alias ticket / lisibilité : même chose que `toFrPhoneInputValue`. */
export const toPhoneInputValue = toFrPhoneInputValue;
